<template>
	<v-container class="pa-0">
		<v-row>
			<v-col cols="12" lg="9" order="2" order-lg="1">
				<GroupHeader></GroupHeader>
				
				<!-- Header com título e botão padronizado -->
				<div v-if="!loading" class="d-flex justify-space-between align-center mb-4 mt-4">
					<h2 :style="{ color: dynamicColor }">Vade-mécum</h2>
					<div>
						<v-btn
							v-if="isAdmin"
							:small="intBreakPoint < 2"
							:color="dynamicColor"
							@click="showDialogCriar = true"
							dark
						>
							<v-icon left>mdi-book-plus</v-icon>
							CRIAR VADE-MÉCUM
						</v-btn>
					</div>
				</div>

				<v-card v-if="vademecumsGrupo.length === 0 && !loading" class="mt-4">
					<v-card-text :class="$vuetify.breakpoint.mobile ? 'text-center py-6 px-4' : 'text-center py-8'">
						<v-icon :size="$vuetify.breakpoint.mobile ? 64 : 80" color="grey lighten-2">mdi-book-open-variant</v-icon>
						<h3 :class="$vuetify.breakpoint.mobile ? 'mt-3 mb-2 grey--text text-h6' : 'mt-4 mb-2 grey--text'">Este grupo ainda não possui um Vade-mécum</h3>
						<p :class="$vuetify.breakpoint.mobile ? 'grey--text body-2 px-2' : 'grey--text'">
							<span v-if="isAdmin">
								Clique no botão acima para criar o Vade-mécum exclusivo deste grupo.
							</span>
							<span v-else>
								Os administradores podem criar um Vade-mécum exclusivo para este grupo.
							</span>
						</p>
					</v-card-text>
				</v-card>

				<v-card v-if="loading" class="mt-4">
					<v-card-text class="text-center py-8">
						<v-progress-circular :color="dynamicColor" indeterminate size="64"></v-progress-circular>
						<p class="mt-4 grey--text">Carregando vade-mécum...</p>
					</v-card-text>
				</v-card>

				<!-- Lista de Vade-mécums -->
				<template v-if="!loading && vademecumsGrupo.length > 0">
					<v-card 
						v-for="vademecum in vademecumsGrupo" 
						:key="vademecum.id"
						class="mt-4">
					<v-card-title :class="$vuetify.breakpoint.mobile ? 'd-flex flex-column align-start pa-4' : 'd-flex justify-space-between align-center'">
						<div class="d-flex align-center mb-2" :class="$vuetify.breakpoint.mobile ? 'mb-2' : ''">
							<v-icon :color="vademecum.cor || dynamicColor" :class="$vuetify.breakpoint.mobile ? 'mr-2' : 'mr-2'">mdi-circle</v-icon>
							<span :style="{ color: dynamicColor }" :class="$vuetify.breakpoint.mobile ? 'text-body-1 font-weight-bold' : ''">{{ vademecum.titulo || 'Vade-mécum do Grupo' }}</span>
						</div>
						<v-btn 
							:small="intBreakPoint < 2"
							style="background-color: transparent !important; box-shadow: none !important; color: #f15a24 !important;"
							@click="abrirDialogVer(vademecum)">
							<v-icon>mdi-eye</v-icon> Ver Detalhes
						</v-btn>
					</v-card-title>
					<v-divider></v-divider>
					<v-card-text :class="$vuetify.breakpoint.mobile ? 'pa-4' : ''">
						<div :class="$vuetify.breakpoint.mobile ? 'mb-3' : 'mb-4'">
							<v-chip :color="dynamicColor" text-color="white" :class="$vuetify.breakpoint.mobile ? 'mr-2 mb-2' : 'mr-2'">
								{{ vademecum.leis ? vademecum.leis.length : 0 }} {{ vademecum.leis && vademecum.leis.length === 1 ? 'Lei' : 'Leis' }}
							</v-chip>
							<v-chip v-if="vademecum.projetos_leis && vademecum.projetos_leis.length > 0" outlined :color="dynamicColor">
								{{ vademecum.projetos_leis.length }} Projetos
							</v-chip>
						</div>
						<p :class="$vuetify.breakpoint.mobile ? 'grey--text mb-0 text-caption' : 'grey--text mb-0'">
							Criado por {{ vademecum.user_created ? `${vademecum.user_created.first_name} ${vademecum.user_created.last_name || ''}` : 'Usuário' }}
						</p>
					</v-card-text>
					</v-card>
				</template>
			</v-col>

			<v-col cols="12" lg="3" order="1" order-lg="2">
				<GroupSideBar />
			</v-col>
		</v-row>

		<!-- Dialog adicionar novo vade-mécum -->
		<DialogAddVademecum
			v-model="showDialogCriar"
			:grupo-id="$route.params.id_grupo"
			@saved="onVademecumGrupoSaved"
		/>

		<!-- Dialog ver leis do vade-mécum -->
		<DialogVerLeis
			v-model="showDialogVer"
			:vademecum="vademecumGrupo || { leis: [], projetos_leis: [] }"
			:is-admin="isAdmin"
			:dynamic-color="dynamicColor"
			@add-lei="showDialogAddLei = true"
			@add-projeto-lei="showDialogAddProjetoLei = true"
			@excluir-lei="removerLeiVademecumGrupo"
			@excluir-projeto-lei="removerProjetoLeiVademecumGrupo"
			@reload="carregarVademecumGrupo"
		/>

		<v-dialog v-model="showDialogEditarInfo" max-width="600px">
			<v-card>
				<v-card-title>
					<v-icon class="mr-2">mdi-pencil</v-icon>
					Editar Informações do Vade-mécum
				</v-card-title>
				<v-card-text>
					<v-text-field
						v-model="vademecumEdit.titulo"
						label="Título do Vade-mécum"
						:color="dynamicColor"
						outlined
					></v-text-field>

					<h3 class="mt-2 mb-2">Cor</h3>
					<v-color-picker
						v-model="vademecumEdit.cor"
						:color="dynamicColor"
						hide-inputs
						show-swatches
						flat
					></v-color-picker>
				</v-card-text>
				<v-card-actions class="d-flex justify-end">
					<v-btn 
						text 
						:color="dynamicColor"
						@click="showDialogEditarInfo = false">
						Cancelar
					</v-btn>
					<v-btn 
						:small="intBreakPoint < 2"
						:color="dynamicColor"
						:loading="salvando"
						class="white--text"
						@click="salvarVademecum">
						<v-icon>mdi-check</v-icon> Salvar
					</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<!-- Dialog para Adicionar Lei -->
		<DialogAddLei
			v-model="showDialogAddLei"
			:vademecum-id="vademecumGrupo ? vademecumGrupo.id : null"
			:vademecum-titulo="vademecumGrupo ? vademecumGrupo.titulo : ''"
			:vademecum-cor="vademecumGrupo ? vademecumGrupo.cor : dynamicColor"
			:leis-existentes="vademecumGrupo ? (vademecumGrupo.leis || []) : []"
			:dynamic-color="dynamicColor"
			:show-advanced-filters="true"
			title="Adicionar Legislação em"
			@lei-adicionada="onLeiAdicionada"
		/>

		<!-- Dialog para Adicionar Projeto de Lei -->
		<DialogAddProjetoLei
			v-model="showDialogAddProjetoLei"
			:vademecum-id="vademecumGrupo ? vademecumGrupo.id : null"
			:vademecum-titulo="vademecumGrupo ? vademecumGrupo.titulo : ''"
			:vademecum-cor="vademecumGrupo ? vademecumGrupo.cor : dynamicColor"
			:projetos-leis-existentes="vademecumGrupo ? (vademecumGrupo.projetos_leis || []) : []"
			:dynamic-color="dynamicColor"
			title="Adicionar Projeto de Lei em"
			@projeto-lei-adicionado="onProjetoLeiAdicionado"
		/>
	</v-container>
</template>

<script>
import GroupSideBar from '@/components/GroupSideBar.vue';
import GroupHeader from '@/components/GroupHeader.vue';
import DialogAddVademecum from '@/components/DialogAddVademecum.vue';
import DialogVerLeis from '@/components/DialogVerLeis.vue';
import DialogAddLei from '@/components/DialogAddLei.vue';
import DialogAddProjetoLei from '@/components/DialogAddProjetoLei.vue';

export default {
	name: 'VademecumGrupo',
	components: {
		GroupSideBar,
		GroupHeader,
		DialogAddVademecum,
		DialogVerLeis,
		DialogAddLei,
		DialogAddProjetoLei,
	},
	data() {
		return {
			loading: false,
			vademecumsGrupo: [],
			vademecumGrupo: null, // Mantido para compatibilidade com dialog de ver detalhes
			grupo: { administradores: [], nome_grupo: '' },
			salvando: false,
			vademecumEdit: { titulo: '', cor: '#F15A24' },
			showDialogCriar: false,
			showDialogVer: false,
			showDialogEditarInfo: false,
			showDialogAddLei: false,
			showDialogAddProjetoLei: false,
			buscaLeis: '',
			searchLeis: '',
			loadingSearch: false,
			leisEncontradas: [],
			// Variáveis para busca de leis durante criação
			searchAddLeis: '',
			internalLeisSearch: [],
			idleis: [],
			loadingInternalSearch: false,
		};
	},
	mounted() {
		this.carregarGrupo();
		this.carregarVademecumGrupo();
	},
	watch: {
		'$route.params.id_grupo'(newId, oldId) {
			if (newId !== oldId) {
				this.carregarGrupo();
				this.carregarVademecumGrupo();
			}
		}
	},
	computed: {
		user() {
			return this.$store.state.user;
		},
		dynamicColor() {
			return this.$route.params.id_grupo === '6f5ff131-006f-4525-9b17-75ae9674a09f' ? '#d8934e' : '#F15A24';
		},
		intBreakPoint() {
			switch (this.$vuetify.breakpoint.name) {
				case 'xs':
					return 0;
				case 'sm':
					return 1;
				case 'md':
					return 2;
				case 'lg':
					return 3;
				case 'xl':
					return 4;
				default:
					return 0;
			}
		},
		isAdmin() {
			if (!this.grupo || !this.grupo.administradores) return false;
			const admin = this.grupo.administradores.find(
				(e) => e.directus_users_id.id === this.user.id
			);
			return !!admin; // Retorna true se encontrou, false caso contrário
		},
		leisFiltradas() {
			if (!this.vademecumGrupo || !this.vademecumGrupo.leis) return [];
			if (!this.buscaLeis) return this.vademecumGrupo.leis;
			
			const termo = this.buscaLeis.toLowerCase();
			return this.vademecumGrupo.leis.filter(lei => {
				const numero = lei.leis_id_lei.numero_lei ? lei.leis_id_lei.numero_lei.toLowerCase() : '';
				const nome = lei.leis_id_lei.nome_lei ? lei.leis_id_lei.nome_lei.toLowerCase() : '';
				return numero.includes(termo) || nome.includes(termo);
			});
		},
	},
	methods: {
		async carregarGrupo() {
			try {
				const grupoId = this.$route.params.id_grupo;
				const { data } = await this.$http.get(
					`items/grupos/${grupoId}?fields=*,administradores.directus_users_id.id,nome_grupo`
				);
				this.grupo = data.data || { administradores: [], nome_grupo: '' };
			} catch (error) {
				window.console.error('Erro ao carregar grupo:', error);
				this.grupo = { administradores: [], nome_grupo: '' };
			}
		},
		async carregarVademecumGrupo() {
			try {
				this.loading = true;
				const grupoId = this.$route.params.id_grupo;
				
				// IMPORTANTE: Buscar todos os vademecums que possuem id_grupo igual ao grupo atual
				// Vademecums pessoais (sem id_grupo) NÃO devem aparecer aqui
				const { data } = await this.$http.get(
					`items/vademecums?fields=*,leis.id,leis.leis_id_lei.*,projetos_leis.*,projetos_leis.projetos_leis_id.*,user_created.id,user_created.first_name,user_created.last_name&filter[id_grupo][_eq]=${grupoId}&sort=-date_created`
				);

				// Atualizar lista de vade-mécums
				if (data.data && data.data.length > 0) {
					this.vademecumsGrupo = data.data;
					// Manter compatibilidade: usar o primeiro para o dialog de detalhes
					this.vademecumGrupo = data.data[0];
					window.console.log('Vade-mécums do grupo carregados:', this.vademecumsGrupo);
				} else {
					this.vademecumsGrupo = [];
					this.vademecumGrupo = null;
					window.console.log('Nenhum vade-mécum encontrado para este grupo');
				}

			} catch (error) {
				window.console.error('Erro ao carregar vade-mécum do grupo:', error);
				this.vademecumsGrupo = [];
				this.vademecumGrupo = null;
			} finally {
				this.loading = false;
			}
		},
		onVademecumGrupoSaved(vademecumData) {
			// Recarregar vade-mécums do grupo após criar
			this.carregarVademecumGrupo();
		},
		async onLeiAdicionada(lei) {
			// Recarregar o vade-mécum após adicionar lei
			await this.carregarVademecumGrupo();
			// Forçar atualização do componente DialogAddLei
			this.$nextTick(() => {
				// A prop leis-existentes será atualizada automaticamente quando vademecumGrupo mudar
			});
		},
		async onProjetoLeiAdicionado(projetoLei) {
			// Recarregar o vade-mécum após adicionar projeto de lei
			await this.carregarVademecumGrupo();
		},
		abrirDialogVer(vademecum) {
			// Se um vade-mécum específico foi passado, usar ele; senão usar o primeiro
			if (vademecum) {
				this.vademecumGrupo = vademecum;
			} else if (this.vademecumsGrupo.length > 0) {
				this.vademecumGrupo = this.vademecumsGrupo[0];
			}
			this.showDialogVer = true;
		},
		abrirDialogEditarInfo() {
			if (!this.vademecumGrupo) return;
			// Criar cópia para edição
			this.vademecumEdit = {
				titulo: this.vademecumGrupo.titulo || '',
				cor: this.vademecumGrupo.cor || this.dynamicColor,
			};
			this.showDialogEditarInfo = true;
		},
		async salvarVademecum() {
			if (!this.isAdmin) {
				alert('Apenas administradores podem editar o Vade-mécum do grupo.');
				this.showDialogEditarInfo = false;
				return;
			}

			if (!this.vademecumEdit.titulo || this.vademecumEdit.titulo.trim() === '') {
				alert('Por favor, preencha o título do vade-mécum.');
				return;
			}

			try {
				this.salvando = true;
				const { data } = await this.$http.patch(
					`items/vademecums/${this.vademecumGrupo.id}`,
					{
						titulo: this.vademecumEdit.titulo,
						cor: this.vademecumEdit.cor,
					}
				);

				if (data.data) {
					this.showDialogEditarInfo = false;
					this.vademecumEdit = { titulo: '', cor: '#F15A24' };
					await this.carregarVademecumGrupo();
				}
			} catch (error) {
				window.console.error('Erro ao salvar vade-mécum:', error);
				alert('Erro ao salvar. Tente novamente.');
			} finally {
				this.salvando = false;
			}
		},
		async removerLei(leiRelacaoId) {
			if (!this.isAdmin) {
				alert('Apenas administradores podem remover leis do Vade-mécum do grupo.');
				return;
			}

			if (!confirm('Deseja realmente remover esta lei do vade-mécum?')) {
				return;
			}

			try {
				await this.$http.delete(`items/vademecums_leis/${leiRelacaoId}`);
				await this.carregarVademecumGrupo();
			} catch (error) {
				window.console.error('Erro ao remover lei:', error);
				alert('Erro ao remover lei. Tente novamente.');
			}
		},
		removerLeiVademecumGrupo(lei, idVadeMecum) {
			// Método chamado pelo componente DialogVerLeis
			lei.deleting = true;
			this.$http
				.patch('/items/vademecums/' + idVadeMecum, {
					leis: { delete: [lei.id] },
				})
				.then(() => {
					this.carregarVademecumGrupo();
					lei.deleting = false;
				})
				.catch((error) => {
					window.console.error('Erro ao remover lei:', error);
					lei.deleting = false;
				});
		},
		removerProjetoLeiVademecumGrupo(lei, idVadeMecum) {
			// Método chamado pelo componente DialogVerLeis
			lei.deleting = true;
			this.$http
				.patch('/items/vademecums/' + idVadeMecum, {
					projetos_leis: { delete: [lei.id] },
				})
				.then(() => {
					this.carregarVademecumGrupo();
					lei.deleting = false;
				})
				.catch((error) => {
					window.console.error('Erro ao remover projeto de lei:', error);
					lei.deleting = false;
				});
		},
		async buscarLeis() {
			if (!this.searchLeis || this.searchLeis.trim() === '') {
				alert('Por favor, digite algo para buscar.');
				return;
			}

			try {
				this.loadingSearch = true;
				this.leisEncontradas = [];
				
				const { data } = await this.$http.get(
					`items/leis?filter[_or][0][numero_lei][_contains]=${this.searchLeis}&filter[_or][1][nome_lei][_contains]=${this.searchLeis}&limit=20&fields=id_lei,numero_lei,nome_lei,esfera_lei,estado_lei,municipio_lei`
				);

				this.leisEncontradas = data.data || [];
			} catch (error) {
				window.console.error('Erro ao buscar leis:', error);
				alert('Erro ao buscar legislações. Tente novamente.');
			} finally {
				this.loadingSearch = false;
			}
		},
		leiJaAdicionada(leiId) {
			if (!this.vademecumGrupo || !this.vademecumGrupo.leis) return false;
			return this.vademecumGrupo.leis.some(l => l.leis_id_lei.id_lei === leiId);
		},
		async adicionarLei(lei) {
			if (!this.isAdmin) {
				alert('Apenas administradores podem adicionar leis ao Vade-mécum do grupo.');
				return;
			}

			try {
				this.$set(lei, 'loading', true);
				
				// Criar a relação diretamente na tabela de relacionamento vademecums_leis
				await this.$http.post('items/vademecums_leis', {
					vademecums_id: this.vademecumGrupo.id,
					leis_id_lei: lei.id_lei
				});

				// Recarregar vade-mécum
				await this.carregarVademecumGrupo();
				
				this.$set(lei, 'loading', false);
			} catch (error) {
				window.console.error('Erro ao adicionar lei:', error);
				alert('Erro ao adicionar legislação. Tente novamente.');
				this.$set(lei, 'loading', false);
			}
		},
		sanitizeTitle(title) {
			if (!title) return '';
			var slug = '';
			var titleLower = title.toLowerCase();
			slug = titleLower.replace(/e|é|è|ẽ|ẻ|ẹ|ê|ế|ề|ễ|ể|ệ/gi, 'e');
			slug = slug.replace(/a|á|à|ã|ả|ạ|ă|ắ|ằ|ẵ|ẳ|ặ|â|ấ|ầ|ẫ|ẩ|ậ/gi, 'a');
			slug = slug.replace(/o|ó|ò|õ|ỏ|ọ|ô|ố|ồ|ỗ|ổ|ộ|ơ|ớ|ờ|ỡ|ở|ợ/gi, 'o');
			slug = slug.replace(/u|ú|ù|ũ|ủ|ụ|ư|ứ|ừ|ữ|ử|ự/gi, 'u');
			slug = slug.replace(/đ/gi, 'd');
			slug = slug.replace(/\s*$/g, '');
			slug = slug.replace(/\s+/g, '-');
			return slug;
		},
		adicionarLeiNaCriacao(item) {
			// Verificar se já está adicionada
			if (this.idleis.includes(item.id_lei)) {
				return;
			}

			// Ativar loading
			this.$set(item, 'loading', true);

			// Simular um pequeno delay para feedback visual melhor
			setTimeout(() => {
				// Adicionar à lista
				this.idleis.push(item.id_lei);
				
				// Desativar loading
				this.$set(item, 'loading', false);

				// Forçar atualização da view
				this.$forceUpdate();
			}, 300);
		},
		clearInternalSearch() {
			this.searchAddLeis = '';
			this.internalLeisSearch = [];
			this.loadingInternalSearch = false;
		},
		async searchLeisInVadeMecum() {
			if (this.searchAddLeis == null) this.searchAddLeis = "";
			
			// Formatar número de lei se for apenas números
			if (!isNaN(this.searchAddLeis) && this.searchAddLeis.indexOf('.') == -1) {
				this.searchAddLeis = this.searchAddLeis.replace(
					/(\d)(?=(\d{3})+$)/g,
					'$1.'
				);
			}

			const searchQuery = this.searchAddLeis
				? `search:"${this.searchAddLeis}", `
				: '';

			const filter = 'filter: { esfera_lei: { _nnull: true}}';

			const query = `
				query FilterByApelidosLeis {
					leis(${filter}${searchQuery}sort: ["-ranking_lei", "-data_lei"], limit: 20, offset: 0) {
					id_lei
					numero_lei
					data_lei
					nome_lei
					estado_lei
					municipio_lei
					esfera_lei
					ranking_lei
					}
				}
			`;

			this.loadingInternalSearch = true;
			this.internalLeisSearch = [];

			try {
				const response = await this.$http.post(
					'https://api.mylegis.com.br/graphql',
					{ query }
				);
				const { leis } = response.data.data;
				this.internalLeisSearch = leis.map(v => ({ ...v, loading: false }));
				this.loadingInternalSearch = false;
			} catch (error) {
				this.loadingInternalSearch = false;
				window.console.error('Erro ao buscar as leis:', error);
			}
		},
	},
};
</script>

<style scoped>
a {
	text-decoration: none;
	color: black !important;
}

a:hover {
	color: #f15a24;
}

h2, h3 {
	font-family: 'Inter';
	font-weight: bold;
	color: #707070;
	text-align: left;
}

.texto-multilinha {
	white-space: normal;
	word-wrap: break-word;
	overflow-wrap: break-word;
	color: #666;
}

.empty-state-box {
	border: 2px dashed #e0e0e0;
	border-radius: 8px;
	background-color: #fafafa;
}

.fullscreen-dialog {
	background-color: white;
}

/* Melhorias Mobile */
@media (max-width: 960px) {
	.v-card-text {
		padding: 16px !important;
	}
	
	.v-card-title {
		padding: 16px !important;
	}
	
	/* Garantir área de toque mínima de 44x44px */
	.v-btn {
		min-height: 44px;
	}
	
	.v-btn--small {
		min-height: 40px;
	}
	
	/* Melhorar espaçamento de chips em mobile */
	.v-chip {
		margin: 2px;
	}
	
	/* Ajustar padding de list items */
	.v-list-item {
		padding-left: 8px;
		padding-right: 8px;
	}
}
</style>
